import {Icon, Tag, Tooltip} from '@/components/atoms'
import {Link} from '@/components-v2'
import {splitUtcToLocalDate} from '@/helpers/dateUtils'
import {TAG_COLOR} from '@/utils/constants/colors'
import {ROUTES} from '@/utils/constants/routesConstants'
import {useMemo} from 'react'
import {useNavigate} from 'react-router-dom'

// Guards against path traversal / injection when building detail routes.
const toSafeRouteParam = value => {
  const raw = String(value ?? '').trim()

  if (!raw || raw.includes('..') || !/^[A-Za-z0-9._-]+$/.test(raw)) {
    return ''
  }

  return encodeURIComponent(raw)
}

// Table-wide text sizing lives in DataTable.scss, so cells can return plain values.
const PromotionLinkCell = ({text, onClick}) =>
  onClick ? <Link useAnchor={false} label={text ?? '-'} onClick={onClick} /> : (text ?? '-')

const DATE_COLUMN_KEYS = new Set(['activeStartDate', 'activeEndDate'])

const formatDisplayValue = (columnKey, rawValue) => {
  if (DATE_COLUMN_KEYS.has(columnKey)) {
    return splitUtcToLocalDate(rawValue) || '-'
  }

  return rawValue ?? '-'
}

export const usePromotionListColumnsV2 = (colConfig, handleCopyClick) => {
  const navigate = useNavigate()

  return useMemo(() => {
    if (!colConfig?.length) return []

    const goToPromotionDetail = promotionId => {
      const safePromotionId = toSafeRouteParam(promotionId)
      return safePromotionId ? () => navigate(ROUTES.PROMOTION_DETAIL.replace(':id', safePromotionId)) : undefined
    }

    const cellDataByColumn = {
      name: ctx => (
        <PromotionLinkCell
          text={ctx.row.original?.name}
          onClick={goToPromotionDetail(ctx.row.original?.id)}
        />
      ),
      noOfStores: ctx => {
        const value = ctx.row.original?.noOfStores
        return value === null || value === undefined ? '-' : value
      },
      percentagePromotionAccepted: ctx => {
        const percentage = ctx.row.original?.percentagePromotionAccepted
        if (percentage === null || percentage === undefined) return '-'
        if (typeof percentage === 'number' && percentage < 90) {
          return <Tag text={`${percentage}%`} color={TAG_COLOR.RED_600} />
        }
        return `${percentage}%`
      },
      status: ctx => {
        const row = ctx.row.original
        const rawStatus = row?.status
        const status = rawStatus ? rawStatus[0] + rawStatus.slice(1).toLocaleLowerCase() : '-'

        return (
          <div className="data-table-icon">
            <span>{status}</span>
            <Tooltip direction="left" text="Duplicate">
              <button
                className="cursor-pointer"
                onClick={e => {
                  e.stopPropagation()
                  handleCopyClick(row?.id, row?.promotionType)
                }}
                title="Duplicate"
              >
                <Icon icon="content_copy" size="big"/>
              </button>
            </Tooltip>
          </div>
        )
      },
    }

    const defaultCellData = columnKey => ctx => formatDisplayValue(columnKey, ctx.row.original?.[columnKey])

    return colConfig.map(col => {
      const columnKey = col.columnKey || col.name_of_db_column || col.id
      const header = col.header || col.label_of_column || col.columnFilterPlaceholder || columnKey

      return {
        id: columnKey,
        columnKey,
        header,
        columnFilterPlaceholder: col.columnFilterPlaceholder || header,
        filterVariant: col.filterVariant || 'text',
        minSize: col.minSize,
        maxSize: col.maxSize,
        initialSize: col.initialSize,
        cellData: cellDataByColumn[columnKey] ?? defaultCellData(columnKey),
      }
    })
  }, [colConfig, navigate, handleCopyClick])
}
